import React, { useEffect, useState } from "react";
import classnames from "classnames";
import * as notify from "../../../shared/notify";

import { Select, Textarea } from "../../../components/form-inputs/inputs";
import { useInput } from "../../../shared/hooks/form-fields";
import { useAssignPriority } from "../hooks/use-assign-priority";
import { useAssignTask } from "../hooks/use-assign-task";
import { sendNote } from "../../../components/notes/lib/data";
import { SECTION_MAP } from "../../../components/task-list/lib/constants";

const BulkSet = ({ availableUserAssignments, disableNotes, disableTeamAssignment, disableUserAssignment, ...props }) => {
    const selectPriorityLevel = useInput("");
    const selectTeam = useInput("");
    const textNote = useInput("");
    const selectAssignment = useInput("");
    const [userAssignmentOptions, setUserAssignmentOptions] = useState([]);
    const { mutate: assignPriority } = useAssignPriority(props.taskType);
    const { mutate: assignTask } = useAssignTask(props.taskType);
    const isProfiling = props.taskType === "scheduling";

    const createUserOptions = (users) => {
        return users
            .map(({ userId, fullName }) => {
                return {
                    text: fullName,
                    value: userId,
                };
            })
            .sort((a, b) => a.text.localeCompare(b.text));
    };

    useEffect(() => {
        if (selectTeam.value) {
            const assignmentGroup = availableUserAssignments.find(({ assignmentGroupKey }) => assignmentGroupKey === selectTeam.value);

            if (assignmentGroup) {
                setUserAssignmentOptions(createUserOptions(assignmentGroup.users));
            } else {
                setUserAssignmentOptions([]);
            }
        }
    }, [selectTeam.value]);

    useEffect(() => {
        selectAssignment.reset();
    }, [userAssignmentOptions]);

    const handleClick = async () => {
        if (props.selected.length <= 0) {
            notify.error("Please select a product first");

            return false;
        }

        if (!selectPriorityLevel.value && !selectTeam.value && !textNote.value) {
            notify.error("Please select a priority level, team assignment, or note");

            return false;
        }

        const selectedContextKeys = !isProfiling ? props.selected.map((s) => s.context.key) : [];
        const selectedTaskKeys = props.selected.map((s) => s.key);
        const selectedTaskTypes = props.selected
            .map(({ taskType }) => taskType)
            .filter((taskType, pos, allTasks) => allTasks.indexOf(taskType) === pos);

        if (selectPriorityLevel.value) {
            assignPriority(
                {
                    priority: selectPriorityLevel.value,
                    taskContextKeys: selectedContextKeys,
                    taskTypes: selectedTaskTypes,
                    taskKeys: selectedTaskKeys,
                },
                {
                    onSuccess: () => {
                        selectPriorityLevel.reset();
                        notify.success("Priority Saved");

                        if (!selectTeam.value && !textNote.value) {
                            props.triggerTableReset();
                        }
                    },
                }
            );
        }

        if (selectTeam.value) {
            assignTask(
                {
                    sourcePage: "assignment",
                    group: selectTeam.value,
                    taskContextKeys: selectedContextKeys,
                    assignedUser: selectAssignment.value,
                    taskTypes: selectedTaskTypes,
                    taskKeys: selectedTaskKeys,
                },
                {
                    onSuccess: (response) => {
                        selectTeam.reset();
                        selectAssignment.reset();

                        if (!response.success) {
                            return notify.error("You do not have permission to perform this action.");
                        }

                        notify.success("Group Saved");

                        if (!textNote.value) {
                            props.triggerTableReset();
                        }
                    },
                }
            );
        }

        if (textNote.value) {
            const notesByKey = props.selected.map((selected) => {
                return {
                    shipmentKey: isProfiling ? selected.key : undefined,
                    shipmentProductKey: isProfiling ? undefined : selected.context.shipmentProductKey,
                    noteText: textNote.value,
                    noteType: isProfiling ? "Shipment" : "Product",
                    taskType: SECTION_MAP[props.taskType].noteKey,
                };
            });
            const notePayload = {
                items: notesByKey,
            };
            const noteResponse = await sendNote(notePayload, "assignment");

            if (noteResponse.length === props.selected.length) {
                notify.success("Notes Saved");
                textNote.reset();
            }

            props.triggerTableReset();
        }
    };

    const buttonClass = classnames("button -primary", { "-disabled": props.selected.length <= 0 });

    return (
        <div className="bulk-set">
            <div className="input-group">
                <Select
                    label="Priority Level"
                    options={[...props.priorityLevels, { text: "Unassign", value: "null" }]}
                    {...selectPriorityLevel.bind}
                />
                <Select
                    label="Assign to Team"
                    options={[{ text: "Available", value: "null" }, ...props.assignments]}
                    {...selectTeam.bind}
                    disabled={disableTeamAssignment}
                />
                <Select
                    disabled={!selectTeam.value || disableUserAssignment}
                    label="Assign to User"
                    options={[{ text: "Available", value: "Available" }, ...userAssignmentOptions]}
                    {...selectAssignment.bind}
                />
            </div>
            <br />
            <div>
                <Textarea
                    label="Note"
                    {...textNote.bind}
                    disabled={disableNotes}
                />
            </div>
            <div className="button-group -right">
                <button
                    className={buttonClass}
                    onClick={handleClick}
                >
                    Assign ({props.selected.length})
                </button>
            </div>
        </div>
    );
};

export default BulkSet;
